import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { authService } from "@/services/auth";
import { onboardingService } from "@/services/onboarding";
import type {
  Doctor,
  LoginCredentials,
  RegisterData,
  AuthResponse,
} from "@/types/auth";
import type { OnboardingData } from "@/services/onboarding";

export const useAuthStore = defineStore("auth", () => {
  const router = useRouter();
  const toast = useToast();

  // State
  const token = ref<string | null>(localStorage.getItem("doctor_token"));
  const doctor = ref<Doctor | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!doctor.value);
  const isVerified = computed(() => doctor.value?.email_verified ?? false);
  const onboardingRequired = computed(
    () => !doctor.value?.onboarding_completed,
  );
  const profileCompletion = computed(
    () => doctor.value?.profile_completion ?? 0,
  );

  // Actions
  const setToken = (newToken: string) => {
    token.value = newToken;
    localStorage.setItem("doctor_token", newToken);
    authService.setAuthToken(newToken);
  };

  const clearAuth = () => {
    token.value = null;
    doctor.value = null;
    localStorage.removeItem("doctor_token");
    authService.setAuthToken("");
    error.value = null;
  };

  const register = async (data: RegisterData): Promise<AuthResponse> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.register(data);

      if (response.success) {
        toast.success(
          response.message ||
            "Registration successful! Please check your email.",
        );

        // Store partial data for verification flow
        if (response.doctor) {
          doctor.value = response.doctor;
        }
      }

      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || "Registration failed";
      error.value = errorMessage;
      toast.error(errorMessage);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const login = async (
    credentials: LoginCredentials,
  ): Promise<AuthResponse> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.login(credentials);

      if (response.success && response.token && response.doctor) {
        setToken(response.token);
        doctor.value = response.doctor;

        toast.success(
          response.message || `Welcome back, Dr. ${response.doctor.name}!`,
        );

        // Redirect based on onboarding status
        if (response.onboarding_required) {
          router.push("/onboarding");
        } else {
          router.push("/dashboard");
        }
      }

      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || "Login failed";
      error.value = errorMessage;

      // Handle specific error cases
      if (err.response?.data?.code === "EMAIL_UNVERIFIED") {
        router.push("/verify-email?email=" + credentials.email);
      } else if (err.response?.data?.code === "PENDING_APPROVAL") {
        // We can either redirect to a special page or just show the message
        // For now, toast is fine, but maybe redirecting to a "Awaiting Approval" static page is better
        toast.info(errorMessage, { timeout: 10000 });
      } else {
        toast.error(errorMessage);
      }

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const verifyEmail = async (
    token: string,
    email?: string,
  ): Promise<AuthResponse> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.verifyEmail(token, email);

      if (response.success && response.login_token && response.doctor) {
        setToken(response.login_token);
        doctor.value = response.doctor;

        toast.success(response.message || "Email verified successfully!");

        // Redirect to onboarding or dashboard
        if (response.redirect_to) {
          const redirectPath = response.redirect_to.startsWith("/")
            ? response.redirect_to.slice(1)
            : response.redirect_to;
          router.push(redirectPath);
        } else if (response.onboarding_required) {
          router.push("/onboarding");
        } else {
          router.push("/dashboard");
        }
      }

      return response;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Email verification failed";
      error.value = errorMessage;
      toast.error(errorMessage);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const resendVerification = async (email: string): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.resendVerification(email);

      if (response.success) {
        toast.success("Verification email sent successfully!");
        return true;
      } else {
        toast.error(response.error || "Failed to send verification email");
        return false;
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Failed to resend verification";
      error.value = errorMessage;
      toast.error(errorMessage);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const sendWhatsAppCode = async (whatsappNumber: string): Promise<any> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.sendWhatsAppCode(whatsappNumber);
      if (response.success) {
        toast.success(response.message || "OTP sent to WhatsApp!");
      }
      return response;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Failed to send WhatsApp code";
      error.value = errorMessage;
      toast.error(errorMessage);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const verifyWhatsAppCode = async (
    whatsappNumber: string,
    otpCode: string,
  ): Promise<any> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.verifyWhatsAppCode(
        whatsappNumber,
        otpCode,
      );
      if (response.success) {
        toast.success(response.message || "WhatsApp verified successfully!");
        if (doctor.value) {
          doctor.value.whatsapp_verified = true;
        }
      }
      return response;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "WhatsApp verification failed";
      error.value = errorMessage;
      toast.error(errorMessage);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const forgotPassword = async (email: string): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.forgotPassword(email);

      if (response.success) {
        toast.success(
          response.message || "Password reset instructions sent to your email",
        );
        return true;
      } else {
        toast.error(response.error || "Failed to send password reset email");
        return false;
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Failed to request password reset";
      error.value = errorMessage;
      toast.error(errorMessage);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const resetPassword = async (
    token: string,
    password: string,
    confirmPassword: string,
  ): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.resetPassword({
        token,
        password,
        confirm_password: confirmPassword,
      });

      if (response.success) {
        toast.success(response.message || "Password reset successfully!");
        router.push("/login");
        return true;
      } else {
        toast.error(response.error || "Password reset failed");
        return false;
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || "Password reset failed";
      error.value = errorMessage;
      toast.error(errorMessage);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchProfile = async (): Promise<void> => {
    if (!token.value) return;

    try {
      const response = await authService.getProfile();

      if (response.success && response.doctor) {
        doctor.value = response.doctor;
      } else {
        clearAuth();
      }
    } catch (err: any) {
      console.error("Failed to fetch profile:", err);
      if (err.response?.status === 401) {
        clearAuth();
      }
    }
  };

  const updateProfile = async (
    profileData: Partial<Doctor>,
  ): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.updateProfile(profileData);

      if (response.success && response.doctor) {
        doctor.value = response.doctor;
        toast.success("Profile updated successfully!");
        return true;
      } else {
        toast.error(response.error || "Profile update failed");
        return false;
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || "Profile update failed";
      error.value = errorMessage;
      toast.error(errorMessage);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const logout = async (): Promise<void> => {
    isLoading.value = true;

    try {
      await authService.logout();
    } catch (err) {
      console.error("Logout error:", err);
    }

    clearAuth();
    toast.info("You have been logged out");
    router.push("/login");
    isLoading.value = false;
  };

  const completeOnboarding = async (data: OnboardingData): Promise<any> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await onboardingService.completeOnboarding(data);

      if (response.success && response.data) {
        // Update doctor data with completed onboarding status
        if (doctor.value) {
          doctor.value = {
            ...doctor.value,
            onboarding_completed: true,
            status: "active",
            profile_completion: 100,
          };
        }

        toast.success(response.message || "Onboarding completed successfully!");
        return response;
      } else {
        toast.error(response.message || "Failed to complete onboarding");
        throw new Error(response.message || "Onboarding failed");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Onboarding completion failed";
      error.value = errorMessage;
      toast.error(errorMessage);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const checkTokenExpiration = (): void => {
    if (!token.value) return;

    try {
      // Decode JWT to check expiration (simple implementation)
      const payload = JSON.parse(atob(token.value.split(".")[1]));
      const currentTime = Date.now() / 1000;

      if (payload.exp && payload.exp < currentTime) {
        toast.warning("Your session has expired. Please log in again.");
        logout();
      }
    } catch (err) {
      console.error("Token validation error:", err);
      logout();
    }
  };

  // Initialize auth service with token if available
  if (token.value) {
    authService.setAuthToken(token.value);
  }

  // Check token expiration on store creation
  checkTokenExpiration();

  return {
    // State
    doctor,
    token,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    isVerified,
    onboardingRequired,
    profileCompletion,

    // Actions
    register,
    login,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    fetchProfile,
    updateProfile,
    completeOnboarding,
    sendWhatsAppCode,
    verifyWhatsAppCode,
    logout,
    checkTokenExpiration,
    setToken,
    clearAuth,
  };
});
